import { formatHistoricalDate, yearsSince } from "../domain/scene-time";
import { buildShareTargets, shareMessage } from "../domain/share";

export interface SceneResponse {
  scene: {
    id: string;
    worldSlug: string;
    year: number;
    slug: string;
    location: string;
    historicalDate: string;
    title: string;
    narrative: string;
    historicalContext: string;
    reconstructionNotes: string;
    videoUrl: string | null;
    posterUrl: string | null;
    sourceType: "seeded" | "user_funded";
    seoStatus: "noindex" | "approved";
    suggestedActions?: string[];
    explorerCount?: number;
    confidenceLabel?: string;
    didYouKnow?: string | null;
    furtherReading?: Array<{ title: string; url: string }>;
  };
  choices: Array<{
    id: string;
    actionLabel: string;
    targetWorldSlug: string;
    targetYear: number;
    targetSlug: string;
  }>;
  disclosure: string;
}

export type ProductKey = string;

export interface OfferView {
  key: ProductKey;
  credits: number;
  amount: string;
  perScene: string;
  currency: string;
}

export interface Discovery {
  pendingActionId: string;
  action: string;
  state: "ready" | "making" | "made" | "failed";
  sourcePath: string;
  scenePath: string | null;
}

export interface DiscoveriesResponse {
  availableCredits: number;
  items: Discovery[];
}

export interface OffersResponse {
  offers: OfferView[];
  sceneDurationSeconds: number;
}

export type ActionOutcome =
  | { kind: "existing"; scenePath: string }
  | { kind: "novel"; pendingActionId: string }
  | { kind: "rejected"; error: string };

export interface GenerationStatus {
  status: string;
  scenePath: string | null;
  failureCode: string | null;
}

export interface PlayerDependencies {
  loadScene: (apiPath: string) => Promise<SceneResponse>;
  submitAction: (scenePath: string, action: string) => Promise<ActionOutcome>;
  startCheckout: (pendingActionId: string, product: ProductKey) => Promise<{ checkoutUrl: string }>;
  requestGeneration: (pendingActionId: string) => Promise<{ generationId: string; status: string }>;
  pollGeneration: (generationId: string) => Promise<GenerationStatus>;
  navigate: (url: string) => void;
  wait: (milliseconds: number) => Promise<void>;
  reportEvent: (type: string, detail?: string) => void;
  loadOffers: () => Promise<OffersResponse>;
  loadDiscoveries: () => Promise<DiscoveriesResponse>;
}

const PREMISE_KEY = "past-unseen:premise";

function readPremiseSeen(): boolean {
  try {
    return localStorage.getItem(PREMISE_KEY) === "seen";
  } catch {
    return true;
  }
}

function rememberPremiseSeen(): void {
  try {
    localStorage.setItem(PREMISE_KEY, "seen");
  } catch {
    // Storage may be unavailable; the premise simply shows again next time.
  }
}

const MAX_POLL_ATTEMPTS = 200;
const POLL_INTERVAL_MS = 3000;

const ACTION_MESSAGES: Record<string, string> = {
  out_of_scope_action: "Past Unseen only reconstructs Amsterdam in 1665. Try something the city could hold.",
  unsupported_world: "Past Unseen only reconstructs Amsterdam in 1665.",
  unsafe_action: "That action cannot be reconstructed here. Try another moment.",
  action_required: "Describe what you would do.",
  action_too_long: "Describe your action in a shorter sentence.",
  action_invalid: "Describe your action in a short sentence.",
  scene_not_found: "This moment is no longer available.",
  action_already_pending: "Someone else is discovering this exact moment right now. Try another action.",
};

function element<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  return node;
}

function paragraph(className: string, text: string): HTMLParagraphElement {
  const node = element("p", className);
  node.textContent = text;
  return node;
}

function renderError(root: HTMLElement, retry: () => void): void {
  root.replaceChildren();
  root.setAttribute("aria-live", "polite");
  const panel = element("section", "error-panel");
  const title = element("h1");
  title.textContent = "This reconstruction could not be loaded";
  const button = element("button");
  button.type = "button";
  button.textContent = "Try again";
  button.addEventListener("click", retry);
  panel.appendChild(title);
  panel.appendChild(paragraph("narrative", "The past is momentarily out of reach."));
  panel.appendChild(button);
  root.appendChild(panel);
}

function renderMedia(scene: SceneResponse["scene"]): HTMLElement {
  const media = element("div", "scene-media");
  if (scene.videoUrl) {
    const video = element("video");
    video.src = scene.videoUrl;
    video.poster = scene.posterUrl ?? "";
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute("aria-label", `Historical reconstruction: ${scene.title}`);
    media.appendChild(video);
  } else if (scene.posterUrl) {
    const poster = element("img");
    poster.src = scene.posterUrl;
    poster.alt = `Historical reconstruction of ${scene.title}`;
    media.appendChild(poster);
  }
  return media;
}

const PROGRESS_LINES = [
  "Reading the harbour records for this moment…",
  "Setting the light over the IJ…",
  "Placing the crowd along the quay…",
  "Dressing the porters and the sailors…",
  "Letting the water move…",
  "Composing your point of view…",
  "Almost there. The scene is being written…",
];

function renderWaiting(root: HTMLElement, message: string, detail?: string): void {
  root.setAttribute("aria-live", "polite");
  let panel = root.querySelector(".reconstruction-status");
  if (!(panel instanceof HTMLElement)) {
    panel = element("section", "reconstruction-status");
    root.appendChild(panel);
  }
  panel.replaceChildren();
  panel.appendChild(paragraph("status-line", message));
  if (detail) panel.appendChild(paragraph("status-detail", detail));
}

export async function mountScenePlayer(
  root: HTMLElement,
  scenePathWithQuery: string,
  dependencies: PlayerDependencies,
): Promise<void> {
  const [scenePath = "", query] = scenePathWithQuery.split("?");
  const params = new URLSearchParams(query ?? "");
  const pendingActionId = params.get("pending_action");
  const justDiscovered = params.get("discovered") === "1";
  const checkoutCancelled = params.get("checkout") === "cancelled";

  const showNotice = (text: string): void => {
    const note = root.querySelector(".action-note");
    if (note instanceof HTMLElement) note.textContent = text;
  };

  const renderDiscoveryOptions = async (form: HTMLElement, pendingId: string): Promise<void> => {
    form.querySelector(".discovery-options")?.remove();
    const options = element("div", "discovery-options");
    options.setAttribute("aria-live", "polite");
    form.appendChild(options);

    let catalog: OffersResponse;
    try {
      catalog = await dependencies.loadOffers();
    } catch {
      catalog = { offers: [], sceneDurationSeconds: 5 };
    }
    if (catalog.offers.length === 0) {
      options.appendChild(paragraph(
        "discovery-note",
        "Amsterdam is not taking new scenes right now. Every existing path is still free to walk.",
      ));
      return;
    }

    options.appendChild(paragraph(
      "discovery-note",
      `Turn your next step into a ${catalog.sceneDurationSeconds}-second AI reconstruction. `
      + "Your scene becomes public for other explorers to visit. "
      + "If it cannot be made, your credit is restored automatically.",
    ));

    const buttons: HTMLButtonElement[] = [];
    const status = paragraph("checkout-status", "");
    status.setAttribute("role", "status");
    const lock = (locked: boolean): void => {
      for (const button of buttons) button.disabled = locked;
    };
    for (const offer of catalog.offers) {
      const button = element("button", "discovery-option");
      button.type = "button";
      button.dataset.product = offer.key;
      button.textContent = offer.credits === 1
        ? `Create this scene · ${offer.amount}`
        : `Create ${offer.credits} scenes · ${offer.amount} (${offer.perScene} each)`;
      button.addEventListener("click", () => {
        if (button.disabled) return;
        lock(true);
        status.textContent = "Opening secure checkout…";
        void dependencies.startCheckout(pendingId, offer.key)
          .then((checkout) => dependencies.navigate(checkout.checkoutUrl))
          .catch(() => {
            lock(false);
            status.textContent = "Checkout could not open. Nothing was charged. Please try again.";
          });
      });
      buttons.push(button);
      options.appendChild(button);
    }
    options.appendChild(status);
  };

  const renderActionForm = (): HTMLFormElement => {
    const form = element("form", "action-form");
    const label = element("label");
    label.htmlFor = "custom-action";
    label.textContent = "Where would you go next?";
    const input = element("input");
    input.id = "custom-action";
    input.name = "action";
    input.type = "text";
    input.maxLength = 280;
    input.autocomplete = "off";
    input.placeholder = "Ask the rope maker about his trade";
    const submit = element("button", "action-submit");
    submit.type = "submit";
    submit.textContent = "Try this";
    const note = element("p", "action-note");
    note.setAttribute("role", "status");

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const action = input.value.trim();
      if (action.length === 0) {
        showNotice(ACTION_MESSAGES.action_required ?? "");
        return;
      }
      showNotice("Checking whether this path already exists…");
      void dependencies.submitAction(scenePath, action)
        .then((outcome) => {
          if (outcome.kind === "existing") {
            dependencies.navigate(outcome.scenePath);
            return;
          }
          if (outcome.kind === "rejected") {
            showNotice(ACTION_MESSAGES[outcome.error] ?? "That action could not be used.");
            form.querySelector(".discovery-options")?.remove();
            return;
          }
          showNotice("No one has explored this here yet.");
          void renderDiscoveryOptions(form, outcome.pendingActionId);
        })
        .catch(() => showNotice("That action could not be sent. Please try again."));
    });

    form.appendChild(label);
    form.appendChild(input);
    form.appendChild(submit);
    form.appendChild(note);
    return form;
  };

  const renderScene = (response: SceneResponse): void => {
    const { scene } = response;
    root.removeAttribute("aria-live");
    root.replaceChildren();
    root.className = "scene-player";

    // Wide screens get a blurred, darkened copy of the clip behind the framed stage.
    if (scene.videoUrl) {
      const backdrop = element("div", "scene-backdrop");
      backdrop.setAttribute("aria-hidden", "true");
      const ambient = element("video");
      ambient.src = scene.videoUrl;
      ambient.autoplay = true;
      ambient.loop = true;
      ambient.muted = true;
      ambient.playsInline = true;
      ambient.setAttribute("aria-hidden", "true");
      ambient.tabIndex = -1;
      backdrop.appendChild(ambient);
      root.appendChild(backdrop);
    }

    const stage = element("div", "scene-stage");
    const media = renderMedia(scene);
    const video = media.querySelector("video");
    stage.appendChild(media);

    const header = element("header", "scene-header");
    const brand = element("a", "brand");
    brand.href = "/";
    brand.textContent = "Past Unseen";
    header.appendChild(brand);
    const controls = element("div", "scene-controls");
    let setSound = (_on: boolean): void => undefined;
    if (video) {
      const sound = element("button", "sound-toggle");
      sound.type = "button";
      sound.textContent = "Sound on";
      sound.setAttribute("aria-pressed", "false");
      setSound = (on: boolean): void => {
        video.muted = !on;
        sound.textContent = on ? "Sound off" : "Sound on";
        sound.setAttribute("aria-pressed", String(on));
        if (on) void video.play().catch(() => undefined);
      };
      sound.addEventListener("click", () => setSound(video.muted));

      // The picture loops; the sound must not. Five seconds of harbour on
      // repeat is grating, so audio plays one pass and then falls quiet.
      let lastTime = 0;
      video.addEventListener("timeupdate", () => {
        if (video.currentTime < lastTime && !video.muted) setSound(false);
        lastTime = video.currentTime;
      });
      controls.appendChild(sound);
    }
    const openShareSheet = (text: string): void => {
      root.querySelector(".share-sheet")?.remove();
      const sheet = element("section", "share-sheet");
      sheet.setAttribute("role", "dialog");
      sheet.setAttribute("aria-label", "Share this scene");
      sheet.appendChild(paragraph("share-preview", text));

      const url = `${location.origin}${scenePath}`;
      const options = element("div", "share-options");
      for (const target of buildShareTargets({ url, text })) {
        if (target.href === null) {
          const copy = element("button", "share-option");
          copy.type = "button";
          copy.dataset.share = target.key;
          copy.textContent = target.label;
          copy.addEventListener("click", () => {
            dependencies.reportEvent("share_clicked", target.key);
            void navigator.clipboard?.writeText(`${text} ${url}`).then(() => {
              copy.textContent = "Link copied";
              dependencies.reportEvent("share_completed", target.key);
            }).catch(() => {
              copy.textContent = "Copy failed";
            });
          });
          options.appendChild(copy);
          continue;
        }
        const link = element("a", "share-option");
        link.dataset.share = target.key;
        link.href = target.href;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.textContent = target.label;
        link.addEventListener("click", () => dependencies.reportEvent("share_clicked", target.key));
        options.appendChild(link);
      }

      if (navigator.share) {
        const native = element("button", "share-option");
        native.type = "button";
        native.dataset.share = "native";
        native.textContent = "More…";
        native.addEventListener("click", () => {
          dependencies.reportEvent("share_clicked", "native");
          void navigator.share?.({ title: scene.title, text, url })
            .then(() => dependencies.reportEvent("share_completed", "native"))
            .catch(() => undefined);
        });
        options.appendChild(native);
      }

      const close = element("button", "share-close");
      close.type = "button";
      close.textContent = "Close";
      close.addEventListener("click", () => sheet.remove());

      sheet.appendChild(options);
      sheet.appendChild(close);
      root.appendChild(sheet);
    };

    const shareButton = (label: string, text: string): HTMLButtonElement => {
      const share = element("button", "share-button");
      share.type = "button";
      share.textContent = label;
      share.setAttribute("aria-label", `Share ${scene.title}`);
      share.addEventListener("click", () => openShareSheet(text));
      return share;
    };
    const inviteText = shareMessage({
      location: scene.location,
      year: scene.year,
      title: scene.title,
      discovered: false,
    });
    controls.appendChild(shareButton("Share", inviteText));
    header.appendChild(controls);

    // Caption: the minimum at rest, the rest behind "More".
    const caption = element("section", "scene-caption");
    const eyebrow = paragraph("eyebrow", `${scene.location} · ${formatHistoricalDate(scene.historicalDate)} `);
    const aiTag = element("span", "ai-tag");
    aiTag.textContent = "AI reconstruction";
    eyebrow.appendChild(aiTag);
    caption.appendChild(eyebrow);
    const title = element("h1");
    title.textContent = scene.title;
    caption.appendChild(title);
    if ((scene.explorerCount ?? 0) >= 10) {
      caption.appendChild(paragraph(
        "explorers",
        `${(scene.explorerCount ?? 0).toLocaleString("en-US")} visits`,
      ));
    }
    const hookRow = element("div", "hook-row");
    const firstSentence = scene.narrative.match(/^[^.!?]+[.!?]/u)?.[0] ?? scene.narrative;
    hookRow.appendChild(paragraph("hook", firstSentence.trim()));
    const more = element("button", "more");
    more.type = "button";
    more.textContent = "About this scene & sources";
    more.setAttribute("aria-expanded", "false");
    more.setAttribute("aria-controls", "scene-details");
    hookRow.appendChild(more);
    caption.appendChild(hookRow);

    const details = element("div", "scene-details");
    details.id = "scene-details";
    details.hidden = true;
    if (scene.narrative.trim() !== firstSentence.trim()) {
      details.appendChild(paragraph("narrative", scene.narrative));
    }
    details.appendChild(paragraph("context", scene.historicalContext));
    if (scene.didYouKnow) {
      const fact = element("p", "did-you-know");
      const label = element("span");
      label.textContent = "Did you know";
      fact.appendChild(label);
      fact.appendChild(document.createTextNode(` ${scene.didYouKnow}`));
      details.appendChild(fact);
    }

    const accuracy = element("details", "accuracy");
    const summary = element("summary");
    summary.textContent = "How accurate is this?";
    accuracy.appendChild(summary);
    accuracy.appendChild(paragraph("confidence", scene.confidenceLabel ?? "Speculative reconstruction"));
    accuracy.appendChild(paragraph(
      "notes",
      `This scene is an AI reconstruction inspired by period sources. The place, trades and materials `
      + `follow what is known of Amsterdam in ${scene.year}. Individual people, faces, dialogue and `
      + `incidental detail are speculative and should not be read as record. `
      + `The date is a narrative setting, not a recorded event.`,
    ));
    if ((scene.furtherReading ?? []).length > 0) {
      const list = element("ul", "further-reading");
      for (const reference of scene.furtherReading ?? []) {
        const item = element("li");
        const link = element("a");
        link.href = reference.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.textContent = reference.title;
        item.appendChild(link);
        list.appendChild(item);
      }
      accuracy.appendChild(list);
    }
    details.appendChild(accuracy);
    details.appendChild(paragraph("disclosure", response.disclosure));
    const legal = element("footer", "scene-legal");
    for (const [href, label] of [
      ["/legal/terms", "Terms"],
      ["/legal/privacy", "Privacy"],
      ["/legal/refunds", "Refunds"],
    ]) {
      const link = element("a");
      link.href = href ?? "/";
      link.textContent = label ?? "";
      legal.appendChild(link);
    }
    details.appendChild(legal);
    more.addEventListener("click", () => {
      if (details.hidden) dependencies.reportEvent("historical_context_opened");
      details.hidden = !details.hidden;
      more.setAttribute("aria-expanded", String(!details.hidden));
      more.textContent = details.hidden ? "About this scene & sources" : "Close";
    });
    caption.appendChild(details);

    // Two small, labelled groups beat one long row: walking on is free and
    // instant, discovering is the thing that creates something new.
    const next = element("nav", "scene-next");
    next.setAttribute("aria-label", "What would you do next?");
    const form = renderActionForm();
    form.hidden = true;

    const walk = element("section", "group-walk");
    walk.appendChild(paragraph("choice-prompt", "What would YOU do next?"));
    const walkRow = element("div", "next-row");
    const pathLink = (choice: SceneResponse["choices"][number]): HTMLAnchorElement => {
      const link = element("a", "choice");
      link.href = `/${choice.targetWorldSlug}/${choice.targetYear}/${choice.targetSlug}`;
      link.textContent = choice.actionLabel;
      link.setAttribute("aria-label", `${choice.actionLabel} in Amsterdam, ${choice.targetYear}`);
      link.addEventListener("click", () => dependencies.reportEvent("continue_journey"));
      return link;
    };
    // Four is enough to choose from; the rest stay one tap away so nothing an
    // explorer paid to create is ever hidden.
    const VISIBLE_PATHS = 4;
    for (const choice of response.choices.slice(0, VISIBLE_PATHS)) walkRow.appendChild(pathLink(choice));
    const hidden = response.choices.slice(VISIBLE_PATHS);
    if (hidden.length > 0) {
      const morePaths = element("button", "more-paths");
      morePaths.type = "button";
      morePaths.textContent = `${hidden.length} more`;
      morePaths.addEventListener("click", () => {
        for (const choice of hidden) walkRow.insertBefore(pathLink(choice), morePaths);
        morePaths.remove();
      });
      walkRow.appendChild(morePaths);
    }
    walk.appendChild(walkRow);

    const discover = element("section", "group-discover");
    discover.appendChild(paragraph("choice-prompt", "Create a scene no one has explored here yet"));
    const discoverRow = element("div", "next-row");
    for (const suggestion of (scene.suggestedActions ?? []).slice(0, 3)) {
      const chip = element("button", "suggestion");
      chip.type = "button";
      chip.textContent = suggestion;
      chip.addEventListener("click", () => {
        const input = form.querySelector<HTMLInputElement>("input[name='action']");
        if (!input) return;
        form.hidden = false;
        input.value = suggestion;
        if (typeof form.requestSubmit === "function") form.requestSubmit();
        else form.dispatchEvent(new Event("submit", { cancelable: true }));
      });
      discoverRow.appendChild(chip);
    }
    const own = element("button", "own");
    own.type = "button";
    own.textContent = "Your own…";
    own.addEventListener("click", () => {
      form.hidden = false;
      form.querySelector<HTMLInputElement>("input[name='action']")?.focus();
    });
    discoverRow.appendChild(own);
    discover.appendChild(discoverRow);
    discover.appendChild(form);

    if (response.choices.length > 0) next.appendChild(walk);
    next.appendChild(discover);

    const panel = element("div", "scene-panel");
    // What this explorer already owns, so nothing paid for is ever lost by
    // leaving the page: a credit to use, a scene being made, a scene made.
    void dependencies.loadDiscoveries().then((owned) => {
      const live = owned.items.filter((item) => item.state !== "failed");
      if (owned.availableCredits === 0 && live.length === 0) return;
      const strip = element("section", "discoveries");
      strip.setAttribute("aria-label", "Your discoveries");
      const summary: string[] = [];
      if (owned.availableCredits > 0) {
        summary.push(`${owned.availableCredits} scene credit${owned.availableCredits === 1 ? "" : "s"} ready`);
      }
      const making = live.filter((item) => item.state === "making").length;
      if (making > 0) summary.push(`${making} being made`);
      strip.appendChild(paragraph("discoveries-title", `Your discoveries${summary.length ? " · " + summary.join(" · ") : ""}`));
      const list = element("ul", "discoveries-list");
      for (const item of live) {
        const entry = element("li");
        const link = element("a");
        link.textContent = item.action;
        link.href = item.state === "made" && item.scenePath !== null
          ? item.scenePath
          : `${item.sourcePath}?pending_action=${item.pendingActionId}`;
        entry.appendChild(link);
        entry.appendChild(document.createTextNode(
          item.state === "made" ? " — made" : item.state === "making" ? " — being made" : " — ready to make",
        ));
        list.appendChild(entry);
      }
      strip.appendChild(list);
      panel.insertBefore(strip, panel.firstChild);
    }).catch(() => undefined);
    if (justDiscovered) {
      const banner = element("section", "discovery-banner");
      banner.setAttribute("aria-live", "polite");
      banner.appendChild(paragraph(
        "discovery-title",
        `Your moment is now part of Amsterdam, ${scene.year}. Everyone can walk into it.`,
      ));
      banner.appendChild(shareButton(
        "Share your discovery",
        shareMessage({ location: scene.location, year: scene.year, title: scene.title, discovered: true }),
      ));
      panel.appendChild(banner);
      details.hidden = false;
      more.setAttribute("aria-expanded", "true");
      more.textContent = "Close";
    }
    panel.appendChild(caption);
    panel.appendChild(next);

    stage.appendChild(panel);
    root.appendChild(stage);
    root.appendChild(header);

    // First visit: one line of premise, and a tap that doubles as the gesture
    // browsers need before a clip may play with sound.
    if (readPremiseSeen() === false) {
      const premise = element("section", "premise");
      premise.setAttribute("role", "dialog");
      premise.setAttribute("aria-label", "Welcome to Past Unseen");
      premise.appendChild(paragraph(
        "premise-eyebrow",
        `Amsterdam · ${formatHistoricalDate(scene.historicalDate)}`,
      ));
      premise.appendChild(paragraph(
        "premise-line",
        `You have travelled ${yearsSince(scene.year, new Date())} years into the past.`,
      ));
      premise.appendChild(paragraph(
        "premise-sub",
        "Explore existing scenes free. Create a new scene when you want to go further; "
        + "it becomes public for every explorer after you.",
      ));
      const actions = element("div", "premise-actions");
      const stepIn = element("button", "step-in");
      stepIn.type = "button";
      stepIn.textContent = video ? "Step in with sound" : "Step in";
      const quiet = element("button", "step-in-quiet");
      quiet.type = "button";
      quiet.textContent = "Quietly";
      const dismiss = (withSound: boolean): void => {
        dependencies.reportEvent("premise_stepped_in", withSound ? "sound" : "quiet");
        rememberPremiseSeen();
        premise.remove();
        root.classList.remove("has-premise");
        if (withSound) setSound(true);
        for (const clip of root.querySelectorAll("video")) void clip.play().catch(() => undefined);
      };
      stepIn.addEventListener("click", () => dismiss(Boolean(video)));
      quiet.addEventListener("click", () => dismiss(false));
      actions.appendChild(stepIn);
      if (video) actions.appendChild(quiet);
      premise.appendChild(actions);
      root.appendChild(premise);
      root.classList.add("has-premise");
    }

    // Muted autoplay is sometimes refused (low-power mode, embedded views). Ask
    // explicitly once mounted and again on the first interaction.
    const nudge = (): void => {
      for (const clip of root.querySelectorAll("video")) void clip.play().catch(() => undefined);
    };
    nudge();
    document.addEventListener("pointerdown", nudge, { once: true });
    document.addEventListener("keydown", nudge, { once: true });
  };

  const followGeneration = async (generationId: string): Promise<void> => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      const progress = await dependencies.pollGeneration(generationId);
      if (progress.status === "succeeded" && progress.scenePath !== null) {
        renderWaiting(root, "Your moment is ready. Stepping in…");
        dependencies.navigate(`${progress.scenePath}?discovered=1`);
        return;
      }
      if (progress.status === "failed") {
        renderWaiting(
          root,
          "This reconstruction could not be completed, and your discovery was not used. "
          + "You can try another action.",
        );
        return;
      }
      renderWaiting(
        root,
        PROGRESS_LINES[Math.min(attempt, PROGRESS_LINES.length - 1)] ?? "Reconstructing…",
        "Usually one to three minutes. Keep watching this scene; you will be taken into yours the moment it exists.",
      );
      await dependencies.wait(POLL_INTERVAL_MS);
    }
    renderWaiting(
      root,
      "This reconstruction is taking longer than expected. Your discovery was not used yet.",
    );
  };

  const run = async (): Promise<void> => {
    root.className = "scene-player loading";
    root.setAttribute("aria-busy", "true");
    try {
      renderScene(await dependencies.loadScene(`/api/scenes${scenePath}`));
    } catch {
      renderError(root, () => void run());
      return;
    } finally {
      root.removeAttribute("aria-busy");
    }

    if (checkoutCancelled) {
      const form = root.querySelector<HTMLFormElement>("form.action-form");
      if (form) form.hidden = false;
      showNotice("Checkout cancelled. Nothing was charged, and your action is saved here whenever you want to continue.");
    }

    if (pendingActionId === null) return;
    renderWaiting(root, "Reconstructing your moment from period sources…", "Usually one to three minutes.");
    try {
      const generation = await dependencies.requestGeneration(pendingActionId);
      await followGeneration(generation.generationId);
    } catch {
      renderWaiting(
        root,
        "Amsterdam is resting for a moment, and your discovery was not used. "
        + "Every existing path is still free to walk. Try again shortly.",
      );
    }
  };

  await run();
}
